import type { RouteLocationNormalized, RouteLocationRaw, RouteRecordRaw } from 'vue-router'

import { routes } from './routes'

import {
  findScreen,
  OPEN_SCREEN_IDS,
  SCREENS,
  type OpenScreenId,
  type ScreenId,
} from '@/generated/screens.g'
import { i18n } from '@/i18n'

interface ScreenRoute {
  readonly name: string
  readonly titleKey: string | undefined
  readonly hasTab: boolean
}

function flatten(records: readonly RouteRecordRaw[]): RouteRecordRaw[] {
  return records.flatMap((record) => [record, ...flatten(record.children ?? [])])
}

let screenRoutes: ReadonlyMap<ScreenId, ScreenRoute> | null = null

function screenRouteTable(): ReadonlyMap<ScreenId, ScreenRoute> {
  screenRoutes ??= new Map(
    flatten(routes).flatMap((record) => {
      const screen = record.meta?.screen
      return screen && typeof record.name === 'string'
        ? [
            [
              screen,
              {
                name: record.name,
                titleKey: record.meta?.title,
                hasTab: record.path.includes(':tab'),
              },
            ] as const,
          ]
        : []
    }),
  )
  return screenRoutes
}

/** True when a string is the id of a catalogue screen. */
export function isScreenId(value: string): value is ScreenId {
  return SCREENS.some((screen) => screen.id === value)
}

/** True when a string is the id of a screen the agent may open. */
export function isOpenScreenId(value: string): value is OpenScreenId {
  return (OPEN_SCREEN_IDS as readonly string[]).includes(value)
}

/** Returns the catalogue screen a resolved route shows, or `null` when it shows none. */
export function screenOf(route: Pick<RouteLocationNormalized, 'meta'>): ScreenId | null {
  return route.meta.screen
}

/** Returns the route that shows a screen, filling its item parameter with `itemId` and its optional `tab` parameter with `tab` when the route has one; `null` when the screen has no route or needs an item id it did not get. */
export function routeForScreen(
  screen: ScreenId,
  itemId?: string,
  tab?: string,
): RouteLocationRaw | null {
  const route = screenRouteTable().get(screen)
  if (!route) return null

  const param = findScreen(screen)?.item?.param
  if (!param) return { name: route.name }
  if (!itemId) return null
  const params: Record<string, string> = { [param]: itemId }
  if (tab && route.hasTab) params.tab = tab
  return { name: route.name, params }
}

/** Returns the translated name of a screen: the title of the route that shows it. */
export function screenLabel(screen: ScreenId): string {
  const key = screenRouteTable().get(screen)?.titleKey
  return key ? i18n.global.t(key) : (findScreen(screen)?.title ?? screen)
}
